import React, { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import "./App.css";

const API_BASE = (process.env.REACT_APP_API_BASE_URL || "").replace(/\/$/, "");
const apiUrl = (path) => `${API_BASE}${path}`;
const SITE_TITLE = (process.env.REACT_APP_SITE_TITLE || "Plugin Reviewer").trim();
const SITE_DESCRIPTION = (
  process.env.REACT_APP_SITE_DESCRIPTION ||
  "Validate your plugin against Moodle official documentation and best practices, then use a structured report to fix issues faster."
).trim();
const GA_MEASUREMENT_ID = (process.env.REACT_APP_GA_MEASUREMENT_ID || "").trim();
const BRAND_URL = (process.env.REACT_APP_BRAND_URL || "https://www.learningsystemsstudio.com/").trim();
const CONTACT_EMAIL = (process.env.REACT_APP_CONTACT_EMAIL || "vl.dovhan@gmail.com").trim();
const LINKEDIN_URL = (
  process.env.REACT_APP_LINKEDIN_URL || "https://www.linkedin.com/in/volodymyr-dovhan-14b15254/"
).trim();
const PRIVACY_URL = (process.env.REACT_APP_PRIVACY_URL || "/privacy.html").trim();
const TERMS_URL = (process.env.REACT_APP_TERMS_URL || "/terms.html").trim();
const COOKIES_URL = (process.env.REACT_APP_COOKIES_URL || "/cookies.html").trim();
const BUG_TRACKER_URL = (
  process.env.REACT_APP_BUG_TRACKER_URL || "https://github.com/volodymyrdovhan/plugin-reviewer-tracker"
).trim();
const KOFI_URL = (process.env.REACT_APP_KOFI_URL || "").trim();
const KOFI_LOGOMARK_URL = "https://storage.ko-fi.com/cdn/logomarkLogo.png";
const APP_VERSION = "0.2.6";
const ZIP_MAX_FILE_MB = Number.parseInt(process.env.REACT_APP_ZIP_MAX_FILE_MB || "50", 10);
const COOKIE_CONSENT_KEY = "reviewer_cookie_consent_v1";
const CAPTCHA_ENABLED = ["1", "true", "yes", "on"].includes(
  (process.env.REACT_APP_CAPTCHA_ENABLED || "").trim().toLowerCase()
);
const CAPTCHA_SITE_KEY = (process.env.REACT_APP_CAPTCHA_SITE_KEY || "").trim();

const GITHUB_NAME_PATTERN = /^[A-Za-z0-9_.-]+$/;

export const parseGithubRepositoryUrl = (url) => {
  const raw = (url || "").trim();
  if (!raw) {
    return null;
  }

  let parsedUrl;
  try {
    parsedUrl = new URL(raw);
  } catch (error) {
    return null;
  }

  if (parsedUrl.protocol !== "https:" || parsedUrl.hostname.toLowerCase() !== "github.com") {
    return null;
  }
  if (parsedUrl.search || parsedUrl.hash) {
    return null;
  }

  const segments = parsedUrl.pathname
    .split("/")
    .filter(Boolean)
    .map((segment) => decodeURIComponent(segment).trim())
    .filter(Boolean);

  if (segments.length < 2) {
    return null;
  }

  const owner = segments[0];
  const repoSegment = segments[1];
  const repo = repoSegment.endsWith(".git") ? repoSegment.slice(0, -4) : repoSegment;
  if (!GITHUB_NAME_PATTERN.test(owner) || !GITHUB_NAME_PATTERN.test(repo)) {
    return null;
  }

  let ref = null;
  if (segments.length > 2) {
    if (segments[2] !== "tree") {
      return null;
    }
    ref = segments.slice(3).join("/").trim();
    if (!ref) {
      return null;
    }
  }

  return { owner, repo, ref };
};

export const repositoryLabelFromUrl = (url, repositoryRef = null) => {
  const parsed = parseGithubRepositoryUrl(url);
  if (parsed?.owner && parsed?.repo) {
    const ref = repositoryRef || parsed.ref;
    return ref ? `${parsed.owner}/${parsed.repo}@${ref}` : `${parsed.owner}/${parsed.repo}`;
  }
  return "Open report";
};

const isHttpUrl = (value) => /^https?:\/\//i.test((value || "").trim());
const SUPPORT_ENABLED = isHttpUrl(KOFI_URL);

const getSupportConfig = () => {
  const url = (process.env.REACT_APP_KOFI_URL || KOFI_URL).trim();
  return {
    url,
    enabled: url === KOFI_URL ? SUPPORT_ENABLED : isHttpUrl(url),
  };
};

const SupportBlock = ({ titleId = "support-title", url }) => (
  <aside className="support-block" aria-labelledby={titleId}>
    <div>
      <h2 id={titleId}>Support the reviewer</h2>
      <p>
        Plugin reviews take server time and ongoing maintenance. The service stays free, but small donations help keep
        it running and improve the checks.
      </p>
    </div>
    <a className="support-button" href={url} target="_blank" rel="noreferrer">
      <img className="support-button-icon" src={KOFI_LOGOMARK_URL} alt="" aria-hidden="true" />
      <span>Support on Ko-fi</span>
    </a>
  </aside>
);

export const sourceLabelFromRepositoryValue = (value, repositoryRef = null) => {
  const raw = (value || "").trim();
  if (!raw) {
    return "Open report";
  }
  if (raw.startsWith("uploaded://")) {
    const filename = raw.slice("uploaded://".length) || "plugin.zip";
    return `Uploaded ZIP: ${filename}`;
  }
  return repositoryLabelFromUrl(raw, repositoryRef);
};

export const validatePublicGithubRepository = async (repositoryUrl) => {
  const parsed = parseGithubRepositoryUrl(repositoryUrl);
  if (!parsed) {
    return {
      ok: false,
      detail:
        "Invalid repository URL. Use https://github.com/{owner}/{repo} or https://github.com/{owner}/{repo}/tree/{ref}",
    };
  }

  const response = await fetch(`https://api.github.com/repos/${parsed.owner}/${parsed.repo}`, {
    headers: {
      Accept: "application/vnd.github+json",
    },
  });

  if (response.status === 404) {
    return {
      ok: false,
      detail: "Repository not found. Check owner/repository name and ensure it is public.",
    };
  }
  if (response.status === 403) {
    return {
      ok: false,
      detail: "Could not validate repository visibility right now (GitHub API limit/restriction). Try again shortly.",
    };
  }
  if (!response.ok) {
    return {
      ok: false,
      detail: `Repository validation failed (GitHub API status ${response.status}).`,
    };
  }

  const payload = await response.json();
  if (payload.private) {
    return {
      ok: false,
      detail: "Repository is private. Submit a public GitHub repository URL.",
    };
  }
  return { ok: true };
};

const parseApiErrorDetail = (detail) => {
  if (!detail) {
    return { text: "Submission failed", guidance: null };
  }
  if (typeof detail === "string") {
    return { text: detail, guidance: null };
  }
  if (typeof detail === "object") {
    const code = (detail.code || "").toString().trim();
    const message = (detail.message || detail.error || code || "Submission failed").toString();
    const authorizationUrl = (detail.authorization_url || "").toString().trim();
    const repository = (detail.repository || "").toString().trim();
    if (code === "awaiting_repo_authorization" && authorizationUrl) {
      return {
        text: message,
        guidance: {
          repository,
          authorizationUrl,
          steps: [
            "Open the installation link.",
            repository
              ? `Select repository "${repository}" during installation.`
              : "Select the repository from your submission.",
            "Complete installation and grant required permissions.",
            "Return to this page and submit the same repository again.",
          ],
        },
      };
    }
    return { text: message, guidance: null };
  }
  return { text: String(detail), guidance: null };
};

const escapeHtml = (value) =>
  (value || "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

const TERMINAL_SUBMISSION_STATUSES = new Set(["succeeded", "failed", "cancelled", "canceled"]);
const REVIEW_PLATFORMS = [
  {
    value: "mp",
    label: "Marketplace",
    title: "Moodle Marketplace",
    url: "https://marketplace.moodle.com/",
  },
  {
    value: "pd",
    label: "Plugins Directory",
    title: "Moodle Plugins Directory",
    url: "https://moodledev.io/general/community/plugincontribution/checklist",
  },
];

export const reviewPlatformLabel = (value) => {
  const platform = REVIEW_PLATFORMS.find((item) => item.value === value);
  return platform ? platform.title : REVIEW_PLATFORMS[0].title;
};

function App() {
  const support = getSupportConfig();
  const [reportSid] = useState(() => {
    const search = new URLSearchParams(window.location.search);
    return (search.get("sid") || "").trim();
  });
  const [form, setForm] = useState({
    repository_url: "",
    email: "",
    name: "",
    marketing_opt_in: false,
    captcha_token: "",
    review_platform: "mp",
    publish_to_github: false,
    publish_mode: "standard",
  });
  const [submissionSource, setSubmissionSource] = useState("repository");
  const [zipFile, setZipFile] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("info");
  const [submissionIdMessage, setSubmissionIdMessage] = useState("");
  const [guidance, setGuidance] = useState(null);
  const [submissionReportUrl, setSubmissionReportUrl] = useState("");
  const [submissionReportLabel, setSubmissionReportLabel] = useState("Open report");
  const [submissionReportDownloadName, setSubmissionReportDownloadName] = useState("");
  const [cookieConsent, setCookieConsent] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportError, setReportError] = useState("");
  const [reportView, setReportView] = useState("preview");
  const [copiedReport, setCopiedReport] = useState(false);
  const [existingSubmission, setExistingSubmission] = useState(null);
  const captchaContainerRef = useRef(null);
  const zipFileInputRef = useRef(null);
  const turnstileWidgetIdRef = useRef(null);
  const reportPreviewRef = useRef(null);
  const shouldRenderTurnstile = CAPTCHA_ENABLED && CAPTCHA_SITE_KEY;

  useEffect(() => {
    document.title = SITE_TITLE;
  }, []);

  useEffect(() => {
    const stored = window.localStorage.getItem(COOKIE_CONSENT_KEY);
    if (stored === "accepted" || stored === "rejected") {
      setCookieConsent(stored);
    }
  }, []);

  useEffect(() => {
    if (!GA_MEASUREMENT_ID || !cookieConsent) {
      return;
    }

    if (cookieConsent === "rejected") {
      window[`ga-disable-${GA_MEASUREMENT_ID}`] = true;
      return;
    }

    window[`ga-disable-${GA_MEASUREMENT_ID}`] = false;
    if (!document.getElementById("ga-script")) {
      const script = document.createElement("script");
      script.id = "ga-script";
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
      document.head.appendChild(script);
    }

    if (!window.dataLayer) {
      window.dataLayer = [];
    }
    if (typeof window.gtag !== "function") {
      window.gtag = function gtag() {
        window.dataLayer.push(arguments);
      };
    }

    window.gtag("js", new Date());
    window.gtag("config", GA_MEASUREMENT_ID);
  }, [cookieConsent]);

  useEffect(() => {
    if (!shouldRenderTurnstile || !captchaContainerRef.current) {
      return undefined;
    }

    let cancelled = false;
    const renderWidget = () => {
      if (cancelled || !window.turnstile || !captchaContainerRef.current) {
        return;
      }
      if (turnstileWidgetIdRef.current !== null) {
        return;
      }

      turnstileWidgetIdRef.current = window.turnstile.render(captchaContainerRef.current, {
        sitekey: CAPTCHA_SITE_KEY,
        callback: (token) => {
          setForm((prev) => ({ ...prev, captcha_token: token || "" }));
        },
        "expired-callback": () => {
          setForm((prev) => ({ ...prev, captcha_token: "" }));
        },
        "error-callback": () => {
          setForm((prev) => ({ ...prev, captcha_token: "" }));
        },
      });
    };

    if (window.turnstile) {
      renderWidget();
      return () => {
        cancelled = true;
      };
    }

    const scriptId = "cf-turnstile-script";
    const existingScript = document.getElementById(scriptId);
    if (existingScript) {
      existingScript.addEventListener("load", renderWidget);
      return () => {
        cancelled = true;
        existingScript.removeEventListener("load", renderWidget);
      };
    }

    const script = document.createElement("script");
    script.id = scriptId;
    script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    script.async = true;
    script.defer = true;
    script.addEventListener("load", renderWidget);
    document.head.appendChild(script);

    return () => {
      cancelled = true;
      script.removeEventListener("load", renderWidget);
    };
  }, [shouldRenderTurnstile]);

  useEffect(() => {
    if (!reportSid) {
      return undefined;
    }

    let cancelled = false;
    let pollTimeoutId = null;
    const loadReport = async (showLoading = false) => {
      if (cancelled) {
        return;
      }
      if (showLoading) {
        setReportLoading(true);
      }
      try {
        const response = await fetch(apiUrl(`/api/v1/submissions/report?sid=${encodeURIComponent(reportSid)}`));
        const payload = await response.json();
        if (!response.ok) {
          const parsed = parseApiErrorDetail(payload?.detail);
          if (!cancelled) {
            setReportError(parsed.text);
          }
          return;
        }
        if (!cancelled) {
          setReportError("");
          setReportData(payload);
          const status = (payload?.status || "").toString().trim().toLowerCase();
          const isDone = Boolean(payload?.report_ready) || TERMINAL_SUBMISSION_STATUSES.has(status);
          if (!isDone) {
            pollTimeoutId = window.setTimeout(() => {
              loadReport(false);
            }, 10000);
          }
        }
      } catch (error) {
        if (!cancelled) {
          setReportError("Failed to load submission report");
          pollTimeoutId = window.setTimeout(() => {
            loadReport(false);
          }, 10000);
        }
      } finally {
        if (!cancelled && showLoading) {
          setReportLoading(false);
        }
      }
    };

    loadReport(true);
    return () => {
      cancelled = true;
      if (pollTimeoutId) {
        window.clearTimeout(pollTimeoutId);
      }
    };
  }, [reportSid]);

  useEffect(() => {
    if (submissionSource !== "repository") {
      setExistingSubmission(null);
      return undefined;
    }

    const email = form.email.trim();
    const repositoryUrl = form.repository_url.trim();
    if (!email || !repositoryUrl) {
      setExistingSubmission(null);
      return undefined;
    }

    let cancelled = false;
    const timeoutId = window.setTimeout(async () => {
      if (cancelled) {
        return;
      }
      try {
        const params = new URLSearchParams();
        if (email) {
          params.set("email", email);
        }
        if (repositoryUrl) {
          params.set("repository_url", repositoryUrl);
        }
        params.set("review_platform", form.review_platform || "mp");
        const response = await fetch(apiUrl(`/api/v1/submissions/lookup?${params.toString()}`));
        const payload = await response.json();
        if (!response.ok || cancelled) {
          return;
        }
        const existing = (payload.items || [])[0] || null;
        setExistingSubmission(existing);
      } catch (error) {
        if (!cancelled) {
          setExistingSubmission(null);
        }
      }
    }, 450);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
  }, [form.email, form.repository_url, form.review_platform, submissionSource]);

  const resetCaptchaToken = () => {
    setForm((prev) => ({ ...prev, captcha_token: "" }));
    if (!shouldRenderTurnstile || turnstileWidgetIdRef.current === null || !window.turnstile) {
      return;
    }
    try {
      window.turnstile.reset(turnstileWidgetIdRef.current);
    } catch (error) {
      // Captcha reset must not break form submission UX.
    }
  };

  const setConsent = (value) => {
    window.localStorage.setItem(COOKIE_CONSENT_KEY, value);
    setCookieConsent(value);
  };

  const onChange = (event) => {
    const { name, value, type, checked } = event.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const onSourceChange = (event) => {
    const nextSource = event.target.value === "zip" ? "zip" : "repository";
    setSubmissionSource(nextSource);
    setMessage("");
    setSubmissionIdMessage("");
    setGuidance(null);
    setExistingSubmission(null);
    setSubmissionReportUrl("");
    setSubmissionReportLabel("Open report");
    setSubmissionReportDownloadName("");

    if (nextSource === "repository") {
      setZipFile(null);
      if (zipFileInputRef.current) {
        zipFileInputRef.current.value = "";
      }
      return;
    }

    setForm((prev) => ({
      ...prev,
      repository_url: "",
      publish_to_github: false,
      publish_mode: "standard",
    }));
  };

  const onZipFileChange = (event) => {
    const nextFile = event.target.files && event.target.files[0] ? event.target.files[0] : null;
    setZipFile(nextFile);
  };

  const copyReportMarkdown = async () => {
    const markdown = reportData?.report_markdown || "";
    if (!markdown) {
      return;
    }
    try {
      await navigator.clipboard.writeText(markdown);
      setCopiedReport(true);
      window.setTimeout(() => setCopiedReport(false), 1500);
    } catch (error) {
      setCopiedReport(false);
    }
  };

  const printReport = () => {
    const markdown = reportData?.report_markdown || "";
    if (!markdown) {
      return;
    }
    const printableBody =
      reportView === "preview"
        ? reportPreviewRef.current?.innerHTML || `<pre>${escapeHtml(markdown)}</pre>`
        : `<pre>${escapeHtml(markdown)}</pre>`;
    const popup = window.open("", "_blank");
    if (!popup) {
      return;
    }
    popup.document.open();
    popup.document.write(`
      <!doctype html>
      <html>
        <head>
          <meta charset="utf-8" />
          <title>Submission Report</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 24px; color: #111827; }
            pre { white-space: pre-wrap; word-break: break-word; }
            code, pre { font-family: ui-monospace, SFMono-Regular, Menlo, monospace; }
            h1,h2,h3 { margin-top: 1.1em; }
          </style>
        </head>
        <body>${printableBody}</body>
      </html>
    `);
    popup.document.close();
    popup.onload = () => {
      popup.focus();
      popup.print();
    };
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);
    setMessage("");
    setMessageType("info");
    setSubmissionIdMessage("");
    setGuidance(null);
    setSubmissionReportUrl("");
    setSubmissionReportLabel("Open report");
    setSubmissionReportDownloadName("");
    try {
      if (submissionSource === "zip") {
        if (!zipFile) {
          setMessageType("error");
          setMessage("Error: Choose a plugin ZIP file before submitting.");
          return;
        }
        if (!zipFile.name.toLowerCase().endsWith(".zip")) {
          setMessageType("error");
          setMessage("Error: Only .zip files are supported.");
          return;
        }
        if (ZIP_MAX_FILE_MB > 0 && zipFile.size > ZIP_MAX_FILE_MB * 1024 * 1024) {
          setMessageType("error");
          setMessage(`Error: ZIP file is too large. Maximum allowed size is ${ZIP_MAX_FILE_MB} MB.`);
          return;
        }

        setMessage("Uploading ZIP and creating submission report...");
        const formData = new FormData();
        formData.append("file", zipFile);
        formData.append("email", form.email);
        if (form.name.trim()) {
          formData.append("name", form.name.trim());
        }
        formData.append("marketing_opt_in", form.marketing_opt_in ? "true" : "false");
        formData.append("review_platform", form.review_platform || "mp");
        if (form.captcha_token) {
          formData.append("captcha_token", form.captcha_token);
        }
        const response = await fetch(apiUrl("/api/v1/submissions/zip"), {
          method: "POST",
          body: formData,
        });
        const payload = await response.json();
        if (!response.ok) {
          const parsed = parseApiErrorDetail(payload?.detail);
          setMessageType("error");
          setMessage(`Error: ${parsed.text}`);
          setSubmissionIdMessage("");
          setGuidance(parsed.guidance);
          resetCaptchaToken();
          return;
        }

        setMessageType("success");
        setMessage("ZIP analysis completed successfully.");
        setSubmissionIdMessage(payload.submission_id || "");
        const reportUrl =
          payload.submission_report_url ||
          `${window.location.origin}${window.location.pathname}?sid=${payload.submission_report_sid}`;
        setSubmissionReportUrl(reportUrl);
        setSubmissionReportLabel("Open ZIP report");
        setSubmissionReportDownloadName("");
        setForm((prev) => ({
          ...prev,
          captcha_token: "",
        }));
        setZipFile(null);
        if (zipFileInputRef.current) {
          zipFileInputRef.current.value = "";
        }
        resetCaptchaToken();
        return;
      }

      setMessage("Validating repository visibility...");
      const repositoryValidation = await validatePublicGithubRepository(form.repository_url);
      if (!repositoryValidation.ok) {
        setMessageType("error");
        setMessage(`Error: ${repositoryValidation.detail}`);
        resetCaptchaToken();
        return;
      }

      setMessage("Repository validated. Creating submission...");
      const response = await fetch(apiUrl("/api/v1/submissions"), {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(form),
      });
      const payload = await response.json();
      if (!response.ok) {
        const parsed = parseApiErrorDetail(payload?.detail);
        setMessageType("error");
        setMessage(`Error: ${parsed.text}`);
        setSubmissionIdMessage("");
        setGuidance(parsed.guidance);
        resetCaptchaToken();
        return;
      }
      setMessageType("success");
      setMessage("Your plugin was added to the review queue.");
      setSubmissionIdMessage(payload.submission_id || "");
      const reportUrl =
        payload.submission_report_url ||
        `${window.location.origin}${window.location.pathname}?sid=${payload.submission_report_sid}`;
      setSubmissionReportUrl(reportUrl);
      setSubmissionReportLabel(repositoryLabelFromUrl(form.repository_url, payload.repository_ref));
      setSubmissionReportDownloadName("");
      setForm((prev) => ({
        ...prev,
        repository_url: "",
        captcha_token: "",
        publish_to_github: false,
        publish_mode: "standard",
      }));
      resetCaptchaToken();
    } catch (error) {
      setMessageType("error");
      setMessage("Error: API unavailable. Try again.");
      setSubmissionIdMessage("");
      resetCaptchaToken();
    } finally {
      setIsSubmitting(false);
    }
  };

  if (reportSid) {
    return (
      <div className="app">
        <header className="topbar">
          <div className="container topbar-inner">
            <a className="brand" href={BRAND_URL} target="_blank" rel="noreferrer">
              <img src="/logo.svg" alt="Learning Systems Studio" className="brand-logo" />
              <span>{SITE_TITLE}</span>
            </a>
            <a className="top-link" href="/">
              New submission
            </a>
          </div>
        </header>

        <main>
          <section className="hero">
            <div className="container">
              <div className="panel submission-report-panel">
                <p className="eyebrow">Submission Report</p>
                {reportLoading ? (
                  <p className="message message-info" role="status" aria-live="polite">
                    Loading submission report...
                  </p>
                ) : null}
                {reportError ? (
                  <p className="message message-error" role="alert">
                    Error: {reportError}
                  </p>
                ) : null}
                {reportData ? (
                  <>
                    <p className="muted">
                      Submission <strong>{reportData.submission_id}</strong> is currently <strong>{reportData.status}</strong>.
                    </p>
                    <p className="muted">
                      Source:{" "}
                      {isHttpUrl(reportData.repository_url) ? (
                        <a href={reportData.repository_url} target="_blank" rel="noreferrer">
                          {sourceLabelFromRepositoryValue(reportData.repository_url, reportData.repository_ref)}
                        </a>
                      ) : (
                        <span>{sourceLabelFromRepositoryValue(reportData.repository_url, reportData.repository_ref)}</span>
                      )}
                    </p>
                    <p className="muted">
                      Review target:{" "}
                      <strong>{reportData.review_platform_label || reviewPlatformLabel(reportData.review_platform)}</strong>
                    </p>
                    {reportData.error_message ? (
                      <p className="message message-error">Processing error: {reportData.error_message}</p>
                    ) : null}
                    {reportData.github_issue_url ? (
                      <p className="message message-success">
                        GitHub issue published:{" "}
                        <a href={reportData.github_issue_url} target="_blank" rel="noreferrer">
                          {reportData.github_issue_url}
                        </a>
                      </p>
                    ) : null}
                    {reportData.report_ready ? (
                      <div className="report-markdown-block">
                        <div className="report-header-row">
                          <div className="report-view-switch">
                            <button
                              type="button"
                              className={reportView === "preview" ? "is-active" : ""}
                              onClick={() => setReportView("preview")}
                              aria-pressed={reportView === "preview"}
                              aria-controls="report-content-preview"
                            >
                              Preview
                            </button>
                            <button
                              type="button"
                              className={reportView === "markdown" ? "is-active" : ""}
                              onClick={() => setReportView("markdown")}
                              aria-pressed={reportView === "markdown"}
                              aria-controls="report-content-markdown"
                            >
                              Markdown
                            </button>
                          </div>
                          <div className="report-actions">
                            <div className="report-copy-group">
                              <button
                                type="button"
                                className="icon-button"
                                onClick={copyReportMarkdown}
                                title="Copy Markdown"
                                aria-label="Copy report markdown"
                              >
                                <i className="fa-regular fa-copy" aria-hidden="true" />
                              </button>
                              <span className="report-copy-hint" role="status" aria-live="polite">
                                {copiedReport ? "Markdown copied" : ""}
                              </span>
                            </div>
                            <button
                              type="button"
                              className="icon-button"
                              onClick={printReport}
                              title="Print report"
                              aria-label="Print report"
                            >
                              <i className="fa-solid fa-print" aria-hidden="true" />
                            </button>
                          </div>
                        </div>
                        {support.enabled ? <SupportBlock titleId="support-title-before-report" url={support.url} /> : null}
                        {reportView === "markdown" ? (
                          <pre id="report-content-markdown">{reportData.report_markdown}</pre>
                        ) : (
                          <div className="report-markdown-preview" ref={reportPreviewRef} id="report-content-preview">
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>{reportData.report_markdown || ""}</ReactMarkdown>
                          </div>
                        )}
                        {support.enabled ? <SupportBlock titleId="support-title-after-report" url={support.url} /> : null}
                      </div>
                    ) : (
                      <p className="message message-info">
                        Report is not ready yet. This page refreshes automatically every 10 seconds.
                      </p>
                    )}
                  </>
                ) : null}
              </div>
            </div>
          </section>
        </main>
      </div>
    );
  }

  return (
    <div className="app">
      <header className="topbar">
        <div className="container topbar-inner">
          <a className="brand" href={BRAND_URL} target="_blank" rel="noreferrer">
            <img src="/logo.svg" alt="Learning Systems Studio" className="brand-logo" />
            <span>{SITE_TITLE}</span>
          </a>
          <a className="top-link" href={BRAND_URL} target="_blank" rel="noreferrer">
            Learning Systems Studio
          </a>
        </div>
      </header>

      <main>
        <section className="hero">
          <div className="container hero-grid">
            <div>
              <p className="eyebrow">Public Beta</p>
              <h1>Submit a Moodle plugin repository or ZIP for an automated technical review.</h1>
              <p className="muted">
                {SITE_DESCRIPTION}
              </p>
              <div className="chips">
                <span>New: Marketplace Reviews</span>
                <span>Moodle Docs-Based Checks</span>
                <span>Best-Practice Findings</span>
                <span>AI-Ready Report</span>
              </div>
            </div>
            <form className="panel" onSubmit={onSubmit} aria-label="Submission form">
              <label htmlFor="submission_source">Submission Source</label>
              <select id="submission_source" name="submission_source" value={submissionSource} onChange={onSourceChange}>
                <option value="repository">Public GitHub Repository URL</option>
                <option value="zip">Plugin ZIP file</option>
              </select>

              <fieldset className="platform-selector">
                <legend>Review Target</legend>
                <div className="platform-options" role="radiogroup" aria-label="Review target">
                  {REVIEW_PLATFORMS.map((platform) => (
                    <label
                      className={`platform-option ${form.review_platform === platform.value ? "is-selected" : ""}`}
                      htmlFor={`review_platform_${platform.value}`}
                      key={platform.value}
                    >
                      <input
                        id={`review_platform_${platform.value}`}
                        name="review_platform"
                        type="radio"
                        value={platform.value}
                        checked={form.review_platform === platform.value}
                        onChange={onChange}
                      />
                      <span className="platform-option-main">
                        <span className="platform-option-label">{platform.label}</span>
                      </span>
                    </label>
                  ))}
                </div>
              </fieldset>

              {submissionSource === "repository" ? (
                <>
                  <label htmlFor="repository_url">Repository URL</label>
                  <input
                    id="repository_url"
                    name="repository_url"
                    type="url"
                    required
                    placeholder="https://github.com/org/moodle-local_demo or /tree/branch-name"
                    value={form.repository_url}
                    onChange={onChange}
                  />
                  <p className="field-hint">
                    Paste a public GitHub repository URL or a branch URL such as
                    {" "}
                    <code>https://github.com/org/moodle-local_demo/tree/feature-branch</code>.
                  </p>
                  {existingSubmission?.submission_report_url ? (
                    <p className="message message-info">
                      A submission already exists for this repository/ref:{" "}
                      <a href={existingSubmission.submission_report_url} target="_blank" rel="noreferrer">
                        {repositoryLabelFromUrl(
                          existingSubmission.repository_url || form.repository_url,
                          existingSubmission.repository_ref
                        )}
                      </a>
                      {" "}
                      for {existingSubmission.review_platform_label || reviewPlatformLabel(existingSubmission.review_platform)}
                      .
                    </p>
                  ) : null}
                </>
              ) : (
                <>
                  <label htmlFor="zip_file">Plugin ZIP file</label>
                  <input
                    id="zip_file"
                    name="zip_file"
                    type="file"
                    accept=".zip,application/zip,application/x-zip-compressed"
                    required
                    onChange={onZipFileChange}
                    ref={zipFileInputRef}
                  />
                  <p className="field-hint">
                    ZIP analysis runs immediately and creates a report page link. Maximum file size:{" "}
                    {ZIP_MAX_FILE_MB} MB.
                  </p>
                </>
              )}

              <label htmlFor="email">Email</label>
              <input
                id="email"
                name="email"
                type="email"
                required
                placeholder="name@example.com"
                value={form.email}
                onChange={onChange}
              />

              <label htmlFor="name">Name (optional)</label>
              <input
                id="name"
                name="name"
                type="text"
                placeholder="Your name"
                value={form.name}
                onChange={onChange}
              />

              {submissionSource === "repository" ? (
                <>
                  <label className="checkbox-row" htmlFor="publish_to_github">
                    <input
                      id="publish_to_github"
                      name="publish_to_github"
                      type="checkbox"
                      checked={form.publish_to_github}
                      onChange={onChange}
                    />
                    Publish findings to GitHub (recommended)
                  </label>

                  {form.publish_to_github ? (
                    <>
                      <label htmlFor="publish_mode">GitHub Publish Mode</label>
                      <select id="publish_mode" name="publish_mode" value={form.publish_mode} onChange={onChange}>
                        <option value="basic">Basic</option>
                        <option value="standard">Standard</option>
                        <option value="detailed">Detailed</option>
                      </select>
                      <p className="field-hint">
                        Basic: single summary issue. Standard: single full summary (no child issues, no code examples).
                        Detailed: full summary with separate child issues and code examples.
                      </p>
                    </>
                  ) : null}
                </>
              ) : null}

              <label className="checkbox-row" htmlFor="marketing_opt_in">
                <input
                  id="marketing_opt_in"
                  name="marketing_opt_in"
                  type="checkbox"
                  checked={form.marketing_opt_in}
                  onChange={onChange}
                />
                I agree to receive product updates
              </label>

              {CAPTCHA_ENABLED ? (
                <>
                  {shouldRenderTurnstile ? (
                    <div id="captcha_widget" ref={captchaContainerRef} />
                  ) : (
                    <>
                      <input
                        id="captcha_token"
                        name="captcha_token"
                        type="text"
                        placeholder="Paste token"
                        value={form.captcha_token}
                        onChange={onChange}
                      />
                    </>
                  )}
                </>
              ) : null}

              <button type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Submitting..." : submissionSource === "zip" ? "Submit ZIP For Review" : "Submit For Review"}
              </button>
              <p className="legal-hint">
                By submitting, you agree to our{" "}
                <a href={TERMS_URL} target="_blank" rel="noreferrer">
                  Terms
                </a>{" "}
                and{" "}
                <a href={PRIVACY_URL} target="_blank" rel="noreferrer">
                  Privacy Policy
                </a>
                . This page uses only essential cookies needed for form submission and security checks.
              </p>
              {message ? (
                <>
                  <p className={`message message-${messageType}`} role={messageType === "error" ? "alert" : "status"}>
                    {message}
                    {submissionIdMessage ? (
                      <>
                        <br />
                        Submission ID: {submissionIdMessage}.
                      </>
                    ) : null}
                    {submissionReportUrl ? (
                      <>
                        <br />
                        Report:{" "}
                        <a
                          href={submissionReportUrl}
                          target="_blank"
                          rel="noreferrer"
                          download={submissionReportDownloadName || undefined}
                        >
                          {submissionReportLabel}
                        </a>
                        .
                      </>
                    ) : null}
                  </p>
                  {guidance ? (
                    <div className="message message-error" role="alert">
                      <p>To continue:</p>
                      <ol>
                        {guidance.steps.map((step) => (
                          <li key={step}>{step}</li>
                        ))}
                      </ol>
                      <p>
                        Install link:{" "}
                        <a href={guidance.authorizationUrl} target="_blank" rel="noreferrer">
                          {guidance.authorizationUrl}
                        </a>
                      </p>
                    </div>
                  ) : null}
                </>
              ) : null}
            </form>
          </div>
        </section>
      </main>
      <footer className="footer">
        <div className="container footer-cta">
          <div className="footer-cta-copy">
            <p className="footer-eyebrow">Contact</p>
            <h2>Ready to ship a compliant Moodle plugin?</h2>
            <p className="footer-muted">Reach out to discuss scope, review readiness, or integrations.</p>
          </div>
          <div className="footer-cta-actions">
            <a className="footer-cta-button footer-cta-email" href={`mailto:${CONTACT_EMAIL}`}>
              Email
            </a>
            <a className="footer-cta-button footer-cta-linkedin" href={LINKEDIN_URL} target="_blank" rel="noreferrer">
              LinkedIn
            </a>
          </div>
        </div>
        <div className="container footer-inner">
          <div className="footer-brand-group">
            <a className="footer-brand" href={BRAND_URL} target="_blank" rel="noreferrer">
              Learning Systems Studio
            </a>
            <span className="footer-note">
              Bug tracker:{" "}
              <a href={BUG_TRACKER_URL} target="_blank" rel="noreferrer">
                plugin-reviewer-tracker
              </a>
            </span>
            {support.enabled ? (
              <span className="footer-note">
                <a href={support.url} target="_blank" rel="noreferrer">
                  Support this service
                </a>
              </span>
            ) : null}
            <span className="footer-version">Version {APP_VERSION}</span>
          </div>
          <span className="footer-note">Moodle™ is a trademark of Moodle Pty Ltd. Not affiliated or endorsed.</span>
        </div>
      </footer>
      {cookieConsent === null ? (
        <div className="cookie-banner" id="cookie-banner" role="dialog" aria-live="polite" aria-label="Cookie consent">
          <div className="cookie-content">
            <p className="cookie-title">Cookies &amp; analytics</p>
            <p className="cookie-text">
              We use optional analytics cookies to understand site traffic. You can accept or reject. See our{" "}
              <a href={COOKIES_URL} target="_blank" rel="noreferrer">
                Cookies Policy
              </a>
              .
            </p>
          </div>
          <div className="cookie-actions">
            <button
              type="button"
              className="button ghost"
              data-cookie-action="reject"
              onClick={() => setConsent("rejected")}
            >
              Reject
            </button>
            <button
              type="button"
              className="button primary"
              data-cookie-action="accept"
              onClick={() => setConsent("accepted")}
            >
              Accept
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

export default App;
